import { sqScalingTemplateStore, sqToolTemplateStore, sqWorkbenchStore } from '@/core/core.stores';
import { setActiveTool } from '@/toolSelection/investigate.actions';
import { TREND_TOOLS } from '@/toolSelection/investigate.constants';
import * as self from '@/tools/models/template.actions';
import {
  createTemplate,
  fetchAllTemplateVersionMetadata,
  fetchFormulasForFeatures,
  fetchSharedTemplateItems,
  fetchTemplateItems,
  fetchTemplatePermissions,
} from '@/tools/models/template.api';
import { INSTANCE_NAME_INPUT_FEATURE, TEMPLATE_TYPES } from '@/tools/models/template.types';
import { mergeVersionMetadata, updateFeatureName } from '@/tools/models/template.utilities';
import {
  filterUnusedFeatures,
  formatTemplateInput,
  getDerivedFeatureId,
} from '@/tools/models/templateFormatting.utilities';
import { getDefaultName } from '@/utilities/formula.utilities';
import { successToast } from '@/utilities/toast.utilities';
import { setHasCreatedModel } from '@/workbench/workbench.actions';
import { fetchTemplates } from '@/workbook/workbook.actions';
import { SeeqNames } from '@seeqdev/constants';
import type { FormulaItemTableOption } from '@/tools/formula/FormulaItemTable.molecule';
import type {
  EnrichedSharedTemplateItem,
  NewlyAddedDerivedFeature,
  NewlyAddedInputFeature,
  TemplateDerivedFeature,
  TemplateInput,
  TemplatePayload,
  TemplateType,
} from '@/tools/models/template.types';
import type { PermissionsV1 } from '@seeqdev/api/model/PermissionsV1';
import type { TemplateOutputV1 } from '@seeqdev/api/model/TemplateOutputV1';

// ─── Helper functions ──────────────────────────────────────────────────────────

/** Checks if a derivedFeatureIndex is referenced in any feature's rules */
const isDerivedFeatureIndexReferenced = (
  targetIndex: number,
  features: Array<TemplateDerivedFeature | NewlyAddedDerivedFeature>,
) => {
  return features.some((feature) =>
    feature.rules?.some((rule) =>
      Array.isArray(rule.inputs)
        ? rule.inputs.some((input) => typeof input === 'object' && input?.derivedFeatureIndex === targetIndex)
        : false,
    ),
  );
};

/** Checks if a new output is referenced by any other new outputs via derivedFeatureIndex.
 * New outputs can reference each other, but if an output is used by another output, it cannot be deleted and must be converted to an intermediate instead.
 */
const isNewOutputUsedByOtherNewOutputs = (featureId: string, derivedFeatures: NewlyAddedDerivedFeature[]) => {
  const targetIndex = derivedFeatures.findIndex((feature) => getDerivedFeatureId(feature) === featureId);
  if (targetIndex < 0) return false;

  const otherOutputs = derivedFeatures.filter((f) => getDerivedFeatureId(f) !== featureId && f.isOutput);
  return isDerivedFeatureIndexReferenced(targetIndex, otherOutputs);
};

/**
 * Checks if a new output is referenced by any existing derived features via derivedFeatureIndex.
 * New outputs can be referenced by existing features when editing formulas.
 */
const isNewOutputUsedByExistingFeatures = (
  outputId: string,
  newDerivedFeatures: NewlyAddedDerivedFeature[],
  existingDerivedFeatures: TemplateDerivedFeature[],
) => {
  const targetIndex = newDerivedFeatures.findIndex((feature) => getDerivedFeatureId(feature) === outputId);
  if (targetIndex < 0) return false;

  return isDerivedFeatureIndexReferenced(targetIndex, existingDerivedFeatures);
};

// ─── Exported actions ──────────────────────────────────────────────────────────

export function resetTemplate(templateType: TemplateType) {
  if (templateType === TEMPLATE_TYPES.TOOL) {
    sqToolTemplateStore.actions.reset();
  } else {
    sqScalingTemplateStore.actions.reset();
  }
}

export async function initializeTemplateWithDefaultName(
  templateType: TemplateType,
  namePrefix: string,
  workbookId: string,
) {
  resetTemplate(templateType);
  const defaultName = await getDefaultName(
    namePrefix,
    workbookId,
    [SeeqNames.API.Flags.ExcludeGloballyScoped],
    [SeeqNames.Types.ModelContainer],
  );
  setTemplate(templateType, { name: defaultName });
}

export function setTemplate(templateType: TemplateType, template: TemplatePayload) {
  if (templateType === TEMPLATE_TYPES.TOOL) {
    sqToolTemplateStore.actions.setTemplate(template);
  } else {
    sqScalingTemplateStore.actions.setTemplate(template);
  }
}

export function setFeatureToEditId(templateType: TemplateType, featureId: string | undefined) {
  if (templateType === TEMPLATE_TYPES.TOOL) {
    sqToolTemplateStore.actions.setFeatureToEditId(featureId);
  } else {
    sqScalingTemplateStore.actions.setFeatureToEditId(featureId);
  }
}

export const getTemplateInputFeatures = (templateType: TemplateType): TemplateInput[] => {
  if (templateType === TEMPLATE_TYPES.TOOL) {
    return sqToolTemplateStore.getState().inputFeatures;
  }
  if (templateType === TEMPLATE_TYPES.SCALING) {
    return sqScalingTemplateStore.getState().inputFeatures;
  }
  return [];
};

export function setInstanceToEditId(templateType: TemplateType, instanceToEditId: string | undefined) {
  if (templateType === TEMPLATE_TYPES.TOOL) {
    sqToolTemplateStore.actions.setInstanceToEditId(instanceToEditId);
  } else {
    sqScalingTemplateStore.actions.setInstanceToEditId(instanceToEditId);
  }
}

export function setTemplateOutput(templateType: TemplateType, templateOutput: TemplateOutputV1) {
  if (templateType === TEMPLATE_TYPES.TOOL) {
    sqToolTemplateStore.actions.setTemplateOutput(templateOutput);
  } else {
    sqScalingTemplateStore.actions.setTemplateOutput(templateOutput);
  }
}

export function setTemplateVersionState(
  templateType: TemplateType,
  versionState: {
    isLocked: boolean;
    isDraft: boolean;
    sourceVersionNumber?: number;
    editBaseFeatureCanonicalIds: string[] | undefined;
  },
) {
  if (templateType === TEMPLATE_TYPES.TOOL) {
    sqToolTemplateStore.actions.setVersionState(versionState);
  } else {
    sqScalingTemplateStore.actions.setVersionState(versionState);
  }
}

export function setTemplateInstanceCount(templateType: TemplateType, instanceCount: number | undefined) {
  if (templateType === TEMPLATE_TYPES.TOOL) {
    sqToolTemplateStore.actions.setInstanceCount(instanceCount);
  } else {
    sqScalingTemplateStore.actions.setInstanceCount(instanceCount);
  }
}

/**
 * Fetches template items for the given workbooks and enriches them with
 * version/draft metadata when the Locking feature is enabled.
 */
export async function fetchEnrichedTemplateItems(workbookIds: string[]) {
  const models = await fetchTemplateItems(workbookIds);
  return enrichWithVersionMetadata(models);
}

/**
 * Fetches shared template items for a given datasource and enriches them with
 * version/draft metadata (when Locking is enabled) and effective permissions.
 */
export async function fetchEnrichedSharedTemplateItems(
  datasourceId: string,
  workbookId: string,
): Promise<EnrichedSharedTemplateItem[]> {
  const models = await fetchSharedTemplateItems(datasourceId, workbookId, true);
  if (models.length === 0) return models;

  const [enrichedModels, permissionsMap] = await Promise.all([
    enrichWithVersionMetadata(models),
    fetchTemplatePermissions(models.map((m) => m.id)).catch(() => new Map<string, PermissionsV1 | undefined>()),
  ]);

  return enrichedModels.map((m) => ({
    ...m,
    canWrite: permissionsMap.get(m.id)?.write,
  }));
}

async function enrichWithVersionMetadata<T extends { id: string }>(models: T[]) {
  // TODO: CRAB-60044 Add back block for lockingEnabled after adding models search endpoint that gets owner informations
  // const lockingEnabled =
  //   isLockingAndVersioningEnabled() &&
  //   hasValidFeatureInLicense(sqLicenseManagementStore.getState().license, Feature.Locking);
  // if (!lockingEnabled || models.length === 0
  if (models.length === 0) {
    return models;
  }

  try {
    const metadataMap = await fetchAllTemplateVersionMetadata(models.map((m) => m.id));
    return mergeVersionMetadata(models, metadataMap);
  } catch {
    return models;
  }
}

/**
 * Creates a new template from the tool pane. If `onPublish` is provided, it's invoked with the new
 * template's id and versionId before post-create side effects (template list refresh, success toast,
 * form reset, navigation). Callers route publish through `usePublishModelVersionMutation` so the
 * version cache stays in sync.
 */
export async function formatInputAndCreateTemplate({
  name,
  description,
  scopedTo,
  newInputFeatures,
  newDerivedFeatures,
  workbookId,
  onPublish,
  onSuccess,
}: {
  name: string;
  description: string;
  scopedTo: string;
  newInputFeatures: NewlyAddedInputFeature[];
  newDerivedFeatures: NewlyAddedDerivedFeature[];
  workbookId: string;
  onPublish?: (modelId: string, versionId: string) => Promise<unknown>;
  onSuccess?: () => void;
}): Promise<void> {
  const formulasByFeatureId = await fetchFormulasForFeatures(newDerivedFeatures);

  const formattedModel = formatTemplateInput({
    name,
    description,
    templateId: '', // No templateId for creation
    scopedTo,
    newInputFeatures,
    existingInputFeatures: [],
    instanceNameFeature: INSTANCE_NAME_INPUT_FEATURE,
    newDerivedFeatures,
    existingDerivedFeatures: [],
    formulasByFeatureId,
  });

  const model = await createTemplate(formattedModel);

  if (onPublish) {
    if (!model.id || !model.versionId) {
      throw new Error('createModel returned a model without an id or versionId; cannot publish.');
    }
    await onPublish(model.id, model.versionId);
  }

  await fetchTemplates([workbookId]);
  successToast({ messageKey: onPublish ? 'TEMPLATES.CREATE_MESSAGE' : 'TEMPLATES.SAVE_DRAFT_SUCCESS' });

  // Set flag to dismiss the "new" badge after first template creation
  if (!sqWorkbenchStore.hasCreatedModel) {
    setHasCreatedModel(true);
  }

  if (onSuccess) {
    onSuccess();
  } else {
    // Default behavior for tool pane
    resetTemplate(TEMPLATE_TYPES.TOOL);
    setActiveTool(TREND_TOOLS.MODELS);
  }
}

/**
 * Renames a derived feature (output or intermediate).
 * Handles existing features (matched by id) and newly added features (matched by referenceId).
 * Updates either existingDerivedFeatures or newDerivedFeatures based on option.isNew.
 *
 * @param newName - The new name for the feature
 * @param option - The FormulaItemTableOption representing the feature to rename
 * @param existingDerivedFeatures - Array of existing derived features
 * @param newDerivedFeatures - Array of newly added derived features (optional)
 * @param templateType - The type of template (default is TOOL)
 */
export function renameDerivedFeature(
  newName: string,
  option: FormulaItemTableOption,
  existingDerivedFeatures: TemplateDerivedFeature[],
  newDerivedFeatures?: NewlyAddedDerivedFeature[],
  templateType: TemplateType = TEMPLATE_TYPES.TOOL,
): void {
  if (option.isNew) {
    if (!newDerivedFeatures) {
      return;
    }
    const updated = newDerivedFeatures.map((feature) => {
      const matchKey = feature.referenceId ?? feature.id;
      return matchKey === option.key ? updateFeatureName(feature, newName) : feature;
    });
    self.setTemplate(templateType, { newDerivedFeatures: updated });
    return;
  }

  const updated = existingDerivedFeatures.map((feature) =>
    feature.id === option.key ? updateFeatureName(feature, newName) : feature,
  );
  self.setTemplate(templateType, { existingDerivedFeatures: updated });
}

/** Deletes a newly added output feature and updates the template's derived features and inputs accordingly. If the
 *  output is used by other new features or by existing features, it will be converted into an intermediate instead of being deleted.
 */
export function deleteNewOutputFeatureAndUpdateDependencies(
  outputId: string,
  derivedFeatures: NewlyAddedDerivedFeature[],
  inputFeatures: NewlyAddedInputFeature[],
  existingDerivedFeatures: TemplateDerivedFeature[],
  templateType: TemplateType = TEMPLATE_TYPES.TOOL,
) {
  if (!derivedFeatures?.length) return;

  const featureToRemove = derivedFeatures.find((feature) => feature.id === outputId);
  if (!featureToRemove) return;

  // Convert to intermediate if used by other new outputs or by existing features
  const usedByNewOutputs = isNewOutputUsedByOtherNewOutputs(outputId, derivedFeatures);
  const usedByExistingFeatures = isNewOutputUsedByExistingFeatures(outputId, derivedFeatures, existingDerivedFeatures);

  if (usedByNewOutputs || usedByExistingFeatures) {
    self.setTemplate(templateType, {
      newDerivedFeatures: derivedFeatures.map((feature) =>
        feature.id === outputId ? { ...feature, isOutput: false, referenceId: feature.id } : feature,
      ),
    });
    return;
  }

  // Remove the output and use the unified removal function to clean up unused dependencies
  const remainingNew = derivedFeatures.filter((f) => f.id !== outputId);
  const { usedNewDerivedFeatures, usedNewInputFeatures } = filterUnusedFeatures([], [], remainingNew, inputFeatures);

  self.setTemplate(templateType, {
    newDerivedFeatures: usedNewDerivedFeatures,
    newInputFeatures: usedNewInputFeatures,
  });
}
